'use client';

import type { CSSProperties } from 'react';
import { Skeleton, SkeletonRegion } from '@/components/ui';
import { useTapTargetHere, type TapTarget } from '@/components/tap-mount-store';
import headStyles from '@/components/content-head.module.css';
import criteriaStyles from '@/components/search/criteria-bar.module.css';
import entryStyles from '@/components/entry.module.css';
import feedStyles from '@/components/feed-view.module.css';
import railStyles from '@/components/checklist-rail.module.css';
import accountStyles from './account/account.module.css';
import activityStyles from './activity/activity.module.css';
import itemStyles from './items/[itemId]/drilldown.module.css';
import sectionStyles from './items/[itemId]/section-screen.module.css';
import lensStyles from './views/lens.module.css';
import listStyles from './boards/list.module.css';
import tagStyles from './tags/tags.module.css';
import landingStyles from './invite/[token]/invite.module.css';
import { SECTION_NAMES, type SectionSegment } from './skeleton-routes';
import { useT } from '@/lib/locale-context';
import styles from './skeletons.module.css';

export { sectionOf, skeletonFor, type SectionSegment } from './skeleton-routes';

/**
 * The routes' loading skeletons (docs/DESIGN_SYSTEM.md "Navigation must be
 * stable": a navigation is acknowledged within a frame by the target's own
 * skeleton). Each one is the surface's frame, drawn with the surface's OWN
 * layout classes and its bands as `Skeleton` shapes, so the content lands into
 * a frame that does not move. No reads. Client components, because the item
 * skeleton draws the tapped row's title from the tap's store
 * (`src/components/tap-mount-store.ts`); every `loading.tsx` stays a server
 * component and renders these as they are.
 *
 * A skeleton has two mounts (docs/LOADING.md §1): the route's (`loading.tsx`,
 * drawn when the router commits) and the tap's (`TapMount`, drawn by the
 * tapped row before the router commits). Both draw the same component, so
 * the handover from one to the other shows no change.
 *
 * The skeletons are the content column only. The top bar's title or trail and
 * the dock while a surface loads are the shell's (`LoadingChrome`,
 * src/components/loading-chrome-ui.tsx): a streamed fallback is not hydrated on a
 * direct visit, so a skeleton's portal or dock claim would never run there.
 */

/** Row widths, in percent, so a column of placeholders reads as text. */
const ROW_WIDTHS = [62, 48, 71, 55, 66, 40, 58, 50];

/** `count` rows in the checklist rail's geometry: 40px (56px on a coarse
 *  pointer), a hairline between rows - the rows a list loads into. */
function SkeletonRows({ count }: { count: number }) {
  return (
    <div className={railStyles.lines} aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={railStyles.line}>
          <Skeleton width={ROW_WIDTHS[i % ROW_WIDTHS.length]} />
        </div>
      ))}
    </div>
  );
}

/** The item body (`ItemDetailBody`): the title, two chips on the status line,
 *  then three door rows on the phone, or three lines of content and the
 *  structure column on a wide container. The full page seats it in its `main`, the peek in
 *  `Modal`.
 *
 *  The title's seat holds the tapped row's title when there is one: `target`
 *  from the tap's mount, or the tap's store when the current route is its
 *  target (the route's mount). A direct visit or a refresh finds the store
 *  empty and draws the shape. */
export function ItemSkeleton({ target }: { target?: TapTarget }) {
  const here = useTapTargetHere();
  const title = (target ?? here)?.title;
  return (
    <SkeletonRegion className={`${itemStyles.body} ${styles.itemRegion}`} testId="skeleton-item">
      <div className={itemStyles.grid}>
        <section className={itemStyles.summary}>
          <div className={itemStyles.summaryHead}>
            {/* The health dot's seat, in its resting grey (HealthDot,
                health-control.tsx), so the title sits where it will land. */}
            <span className={itemStyles.titleDot} aria-hidden>
              <span className={itemStyles.signalDot} />
            </span>
            {title ? (
              <div
                className={`${itemStyles.title} ${styles.titleLine}`}
                data-testid="skeleton-title"
              >
                {title}
              </div>
            ) : (
              <div className={`${itemStyles.title} ${styles.titleLine}`}>
                <Skeleton width={55} />
              </div>
            )}
          </div>
          <div className={itemStyles.statusLine}>
            <span className={styles.chipSlot}>
              <Skeleton shape="block" width="64px" height="var(--chip-h-md)" />
            </span>
            <span className={styles.chipSlot}>
              <Skeleton shape="block" width="84px" height="var(--chip-h-md)" />
            </span>
          </div>
          {/* The phone's doors (`PhoneDoors`): rows under the head, drawn
              only where the page draws them. */}
          <div className={itemStyles.phoneDoors}>
            <div className={styles.detailRows}>
              {[28, 36, 24].map((width) => (
                <div key={width} className={styles.detailRow}>
                  <Skeleton width={width} />
                </div>
              ))}
            </div>
          </div>
          {/* The sections in place, the wide page's content band. */}
          <div className={itemStyles.inPlace}>
            <div className={styles.content}>
              <Skeleton width={92} />
              <Skeleton width={86} />
              <Skeleton width={60} />
            </div>
          </div>
        </section>
        {/* The structure column (the lineage minimap and the relationships), a
            label line and four rows; only a wide container shows it. */}
        <div
          className={`${itemStyles.structure} ${styles.structure}`}
          data-testid="skeleton-structure"
        >
          <Skeleton width={30} />
          <div className={styles.detailRows}>
            {[46, 38, 52, 30].map((width) => (
              <div key={width} className={styles.detailRow}>
                <Skeleton width={width} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** A detail-section screen (`SectionScreen`: Activity, Relationships, Proposed
 *  changes): the section's name and the item's under it, then six rows. The
 *  name is text, since the route knows it (Leon, 2026-09-29: a skeleton draws
 *  every text the device already knows). The item's title is text where the
 *  tap's store holds it (the last crumb of the trail the item page gave its
 *  door), and a shape otherwise. */
export function SectionSkeleton({
  section,
  target,
}: {
  section: SectionSegment;
  target?: TapTarget;
}) {
  const t = useT();
  const here = useTapTargetHere();
  const subject = (target ?? here)?.trail?.items.at(-1)?.title;
  return (
    <SkeletonRegion testId="skeleton-section">
      <div className={sectionStyles.head}>
        {section === 'sections' ? (
          <div className={sectionStyles.title}>
            <Skeleton width={30} />
          </div>
        ) : (
          <div className={sectionStyles.title} data-testid="skeleton-section-name">
            {t(SECTION_NAMES[section])}
          </div>
        )}
        {subject ? (
          <p className={sectionStyles.subject} data-testid="skeleton-section-subject">
            {subject}
          </p>
        ) : (
          <div className={sectionStyles.subject}>
            <Skeleton width={40} />
          </div>
        )}
      </div>
      <SkeletonRows count={6} />
    </SkeletonRegion>
  );
}

/** The lens rows' widths, in percent of the title cell. */
const LENS_TITLE_WIDTHS = [58, 44, 66, 51, 38, 62, 47, 55, 41, 60, 49, 53];

/** How many rows the lens and activity skeletons draw: the first screen, and
 *  no more (a skeleton does not scroll). */
const LENS_ROWS = 12;
const ACTIVITY_ROWS = 10;

/** The lens table's track: the title, a context column, the person. The real
 *  track is the lens's columns; only the title's left edge and the row's
 *  height matter to the handover. On the phone the same cells stack into the
 *  row's lines. */
const LENS_GRID = { '--grid': 'minmax(200px, 1fr) 180px 140px' } as CSSProperties;

/**
 * A lens (`LensSurface` on its list rail: `/work` and `/views/<id>`): the
 * content head (the filter field, the arrange icons, the create), then the
 * table in the list row anatomy (docs/DESIGN_SYSTEM.md "List row anatomy"):
 * the column head, one group heading (the standard Work lens groups its rows),
 * then rows with the glyph's seat and a title line on line one, an avatar
 * block at its end, and a context line on every other row. Drawn in the
 * table's own classes (`list.module.css`), so the phone's three-line row and
 * the desktop grid both come from the real rules.
 */
export function LensSkeleton() {
  const t = useT();
  return (
    <main className={lensStyles.main}>
      <SkeletonRegion className={styles.rounded} testId="skeleton-lens">
        <div className={headStyles.head}>
          <div className={headStyles.filter}>
            {/* The empty box, in the bar's own classes: as tall as the
                real one at every width (docs/LOADING.md). */}
            <div className={criteriaStyles.bar}>
              <div className={criteriaStyles.box}>
                <Skeleton width={40} />
              </div>
            </div>
          </div>
          <div className={`${headStyles.arrange} ${styles.lensArrange}`}>
            <Skeleton shape="block" />
            <Skeleton shape="block" />
            {/* The Display menu: desktop only on the list rail. */}
            <Skeleton shape="block" className={styles.wideOnly} />
          </div>
          <div className={headStyles.primary}>
            <Skeleton shape="block" width="104px" height="var(--control-h-md)" />
          </div>
        </div>
        <div className={listStyles.table} style={LENS_GRID} aria-hidden>
          <div className={listStyles.headRow}>
            <span className={listStyles.headCell}>
              <span className={listStyles.headBtn}>
                <span className={listStyles.headLabel}>{t('shell.skeleton.titleColumn')}</span>
              </span>
            </span>
          </div>
          <div className={listStyles.rows}>
            <div className={listStyles.sectionRow}>
              <span className={listStyles.sectionCell}>
                <span className={listStyles.sectionBtn}>
                  <span className={listStyles.sectionLabel}>
                    <Skeleton width="72px" />
                  </span>
                </span>
              </span>
            </div>
            {LENS_TITLE_WIDTHS.slice(0, LENS_ROWS).map((width, i) => (
              <div key={i} className={listStyles.row} data-testid="skeleton-lens-row">
                <div className={listStyles.top}>
                  <span
                    className={`${listStyles.cell} ${listStyles.titleCell}`}
                    style={{ '--col': 1 } as CSSProperties}
                  >
                    <span className={listStyles.glyph}>
                      <span className={listStyles.dot} />
                    </span>
                    <span className={`${listStyles.title} ${styles.titleLine}`}>
                      <Skeleton width={width} />
                    </span>
                  </span>
                  <span className={listStyles.cell} style={{ '--col': 3 } as CSSProperties}>
                    <Skeleton shape="block" width="18px" height="18px" className={styles.avatar} />
                  </span>
                </div>
                {i % 2 === 1 && (
                  <div className={listStyles.context}>
                    <span className={listStyles.cell} style={{ '--col': 2 } as CSSProperties}>
                      <Skeleton width={`${60 + ((i * 17) % 50)}px`} />
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </SkeletonRegion>
    </main>
  );
}

/** Activity (`/activity`, the one feed): the level filter's seat, then ten
 *  entries in the feed's own row (`feed-view.module.css`) and the entry's own
 *  columns (`entry.module.css`): the actor's avatar block, the item's line
 *  beside it, and the content's line under that. On a wide viewport the
 *  split's detail pane keeps its placeholder sentence, text the page always
 *  shows before an entry is opened. */
export function ActivitySkeleton() {
  const t = useT();
  return (
    <div className={activityStyles.page}>
      <div className={activityStyles.split} data-mode="index">
        <div className={activityStyles.feedPane}>
          <SkeletonRegion
            className={`${feedStyles.feed} ${styles.rounded}`}
            testId="skeleton-activity"
          >
            <div className={feedStyles.toolbar}>
              <Skeleton shape="block" width="180px" />
            </div>
            <ul className={feedStyles.list} aria-hidden>
              {Array.from({ length: ACTIVITY_ROWS }, (_, i) => (
                <li key={i} className={feedStyles.row} data-testid="skeleton-activity-row">
                  <div className={feedStyles.main}>
                    <div className={entryStyles.entry} data-head="true">
                      <div className={entryStyles.head}>
                        <Skeleton width={ROW_WIDTHS[i % ROW_WIDTHS.length]} />
                      </div>
                      <span className={entryStyles.lead}>
                        <Skeleton
                          shape="block"
                          width="20px"
                          height="20px"
                          className={styles.avatar}
                        />
                      </span>
                      <span className={entryStyles.content}>
                        <Skeleton width={`${90 + ((i * 23) % 80)}px`} />
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </SkeletonRegion>
        </div>
        <div className={activityStyles.detailPane}>
          <p className={activityStyles.placeholder}>{t('shell.skeleton.activityDetail')}</p>
        </div>
      </div>
    </div>
  );
}

/** The floor for every other route: a head line, then eight rows, in the lens
 *  page's frame (the Work page is the route most often landed on). */
export function FloorSkeleton() {
  return (
    <main className={lensStyles.main}>
      <SkeletonRegion testId="skeleton-page">
        <div className={`${headStyles.head} ${styles.floorHead}`}>
          <Skeleton width={30} />
        </div>
        <SkeletonRows count={8} />
      </SkeletonRegion>
    </main>
  );
}

/** The landing card (/connect, docs/AGENT_ACCESS.md §6a): a heading, two
 *  lines of sentence, the button row, in the card the page draws. */
export function ConnectSkeleton() {
  return (
    <div className={landingStyles.wrap}>
      <SkeletonRegion testId="skeleton-page">
        <div className={landingStyles.card}>
          <Skeleton width={80} />
          <Skeleton width={95} />
          <Skeleton width={60} />
          <Skeleton width={30} />
        </div>
      </SkeletonRegion>
    </div>
  );
}

/** The tag explorer's rail (`TagRail`): the filter's seat, then eight tree
 *  rows. The layout's Suspense fallback while the tree is read. */
export function TagRailSkeleton() {
  return (
    <SkeletonRegion testId="skeleton-tag-rail">
      <Skeleton shape="block" height="var(--control-h-sm)" />
      <SkeletonRows count={8} />
    </SkeletonRegion>
  );
}

/** The selected tag's column (the /tags page): the head's path, two fact
 *  rows, then eight item rows. The route's `loading.tsx`, drawn inside the
 *  layout's frame, so a selection keeps the rail. */
export function TagColumnSkeleton() {
  return (
    <SkeletonRegion className={tagStyles.columnBody} testId="skeleton-tag-column">
      <div className={tagStyles.head}>
        <Skeleton width="min(240px, 50%)" />
      </div>
      <Skeleton width={30} />
      <Skeleton width={45} />
      <SkeletonRows count={8} />
    </SkeletonRegion>
  );
}

/** The whole explorer frame, rail and column (`TagsFrame`'s geometry): the
 *  tap's mount for a `/tags` target. */
export function TagsSkeleton() {
  return (
    <main className={tagStyles.main}>
      <aside className={tagStyles.rail}>
        <TagRailSkeleton />
      </aside>
      <section className={tagStyles.column}>
        <TagColumnSkeleton />
      </section>
    </main>
  );
}

/** One group of the account page: its heading's seat, then `rows` rows in
 *  the row primitive's height. */
function AccountGroupSkeleton({ rows }: { rows: number }) {
  return (
    <div className={accountStyles.group}>
      <Skeleton width={20} />
      <SkeletonRows count={rows} />
    </div>
  );
}

/** The account page (/account): the head (avatar, name, the mono line), then
 *  the four groups in the page's own columns. */
export function AccountSkeleton() {
  return (
    <main className={accountStyles.main}>
      <SkeletonRegion testId="skeleton-page">
        <div className={accountStyles.head}>
          <Skeleton shape="block" height="44px" />
          <div className={accountStyles.headText}>
            <Skeleton width="min(160px, 40vw)" />
            <Skeleton width="min(260px, 60vw)" />
          </div>
        </div>
        <div className={accountStyles.columns}>
          <div className={accountStyles.column}>
            <AccountGroupSkeleton rows={4} />
            <AccountGroupSkeleton rows={2} />
          </div>
          <div className={accountStyles.column}>
            <AccountGroupSkeleton rows={3} />
            <AccountGroupSkeleton rows={2} />
          </div>
        </div>
      </SkeletonRegion>
    </main>
  );
}

/** The contacts screen (/account/contacts): the add field's seat, then the
 *  list's rows, in the screen's column. */
export function AccountContactsSkeleton() {
  return (
    <main className={accountStyles.main}>
      <SkeletonRegion className={accountStyles.contactsColumn} testId="skeleton-page">
        <Skeleton shape="block" width="100%" height="var(--control-h-md)" />
        <SkeletonRows count={6} />
      </SkeletonRegion>
    </main>
  );
}
